// Partes comunes de los reportes de soporte (D95): encabezado con periodo y Excel, datos de casos y horas del periodo
// (desde el inicio del periodo anterior, para comparar) y las hojas de Excel de casos.
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { casosParaReporte, textoEspera, type Caso } from '../../../api/casos';
import { horasEntre, horasTexto, type RegistroHoras } from '../../../api/proyectos';
import { useMiembros } from '../../../data/queries';
import { shortDate } from '../../../lib/format';
import { areaDe, minutosResolucion, minutosRespuesta, PERIODOS_R, rangoR, type DetalleCasos, type PeriodoR } from '../../../lib/reportesSoporte';
import type { Celda, Hoja } from '../../../lib/xlsx';
import { BotonExcel, nombrePeriodo } from '../../../ui/BotonExcel';

export const fmtMin = (m: number | null) => (m == null ? '—' : textoEspera(m));
export const fmtHoras = (m: number) => horasTexto(Math.round(m));
export const PRIORIDAD: Record<string, string> = { URGENTE: 'Urgente', ALTA: 'Alta', NORMAL: 'Normal', BAJA: 'Baja' };
export const ESTADO: Record<string, string> = { NUEVO: 'Nuevo', ABIERTO: 'Abierto', PENDIENTE: 'Esperando al cliente', RESUELTO: 'Resuelto', CERRADO: 'Cerrado' };
export const ORIGEN: Record<string, string> = { CORREO: 'Correo', WHATSAPP: 'WhatsApp', TELEFONO: 'Teléfono', ZAMMAD: 'Zammad', MANUAL: 'Creado a mano', PORTAL: 'Portal' };
const nombreDe = (m: Record<string, string>, k: string) => m[k] ?? (k ? k.charAt(0) + k.slice(1).toLowerCase() : 'Otro');
export const origenTexto = (k: string) => nombreDe(ORIGEN, k);
export const prioridadTexto = (k: string) => nombreDe(PRIORIDAD, k);

export function CabeceraReporte({ titulo, rango, periodo, setPeriodo, archivo, hojas, listo, children }: { titulo: string; rango: { desde: Date; hasta: Date }; periodo: PeriodoR; setPeriodo: (p: PeriodoR) => void; archivo: string; hojas: () => Hoja[]; listo: boolean; children?: ReactNode }) {
  return (
    <header className="vhead">
      <div>
        <h1>{titulo}</h1>
        <p className="sub">
          {shortDate(rango.desde.toISOString())} – {shortDate(new Date(rango.hasta.getTime() - 1).toISOString())} · comparado con el periodo anterior
        </p>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {children}
        <div className="seg" role="group" aria-label="Periodo">
          {PERIODOS_R.map(([v, t]) => (
            <button key={v} aria-pressed={periodo === v} onClick={() => setPeriodo(v)}>
              {t}
            </button>
          ))}
        </div>
        <BotonExcel archivo={nombrePeriodo(archivo, rango.desde, rango.hasta)} hojas={hojas} disabled={!listo} />
      </div>
    </header>
  );
}

/** Casos y horas desde el inicio del periodo anterior (una sola descarga para el periodo y su comparacion). */
export function useDatosSoporte(periodo: PeriodoR) {
  const rango = rangoR(periodo);
  const q = useQuery({
    queryKey: ['reporte-soporte', periodo, rango.desde.toDateString()],
    queryFn: async () => {
      const [casos, horas] = await Promise.all([casosParaReporte(rango.antes), horasEntre(rango.antes, rango.hasta)]);
      return { casos, horas };
    },
    staleTime: 5 * 60_000,
  });
  return { q, rango };
}

export function usePersona() {
  const miembros = useMiembros();
  return (id: string | null) => (id ? (miembros.data?.find((m) => m.id === id)?.nombre ?? 'Otra persona') : 'Sin asignar');
}

/** Hoja "Casos": un caso por fila con sus tiempos y las horas registradas en el periodo. */
export function hojaCasos(nombre: string, lista: Caso[], horas: RegistroHoras[], desde: Date, hasta: Date, persona: (id: string | null) => string): Hoja {
  const en = (iso: string | null) => !!iso && Date.parse(iso) >= desde.getTime() && Date.parse(iso) < hasta.getTime();
  const minutos = new Map<string, number>();
  for (const h of horas) if (h.casoId && en(h.fecha)) minutos.set(h.casoId, (minutos.get(h.casoId) ?? 0) + h.minutos);
  const delPeriodo = lista.filter((c) => en(c.creado) || en(c.cerradoEn) || c.estado === 'NUEVO' || c.estado === 'ABIERTO' || c.estado === 'PENDIENTE');
  return {
    nombre,
    filas: [
      ['Caso', 'Asunto', 'Empresa', 'Área', 'Estado', 'Prioridad', 'Origen', 'Asignado', 'Creado', 'Primera respuesta (min)', 'Cerrado', 'Resolución (h)', 'Horas registradas', 'Entró en el periodo'],
      ...delPeriodo.map((c): Celda[] => {
        const res = minutosResolucion(c);
        return [
          c.numero != null ? String(c.numero) : '',
          c.asunto,
          c.empresa?.nombre ?? '',
          areaDe(c),
          ESTADO[c.estado] ?? c.estado,
          prioridadTexto(c.prioridad),
          origenTexto(c.origen),
          persona(c.asignadoId),
          new Date(c.creado),
          minutosRespuesta(c),
          c.cerradoEn ? new Date(c.cerradoEn) : null,
          res == null ? null : Math.round((res / 60) * 100) / 100,
          minutos.has(c.id) ? Math.round((minutos.get(c.id)! / 60) * 100) / 100 : null,
          en(c.creado) ? 'Sí' : 'No',
        ];
      }),
    ],
  };
}

/** Filas de resumen (Dato | Valor | Periodo anterior) de un detalle de casos. */
export function filasResumen(d: DetalleCasos, a: DetalleCasos | null): Celda[][] {
  return [
    ['Dato', 'Este periodo', 'Periodo anterior'],
    ['Casos que entraron', d.entraron, a?.entraron],
    ['Resueltos', d.resueltos, a?.resueltos],
    ['Abiertos hoy', d.abiertos, null],
    ['Esperando al cliente', d.pendientes, null],
    ['Primera respuesta, mediana (min)', d.respuestaMediana, a?.respuestaMediana],
    [`Dentro del SLA de 30 min (%)`, d.dentroSla, a?.dentroSla],
    ['Resolución, mediana (h)', d.resolucionMediana == null ? null : Math.round((d.resolucionMediana / 60) * 10) / 10, a?.resolucionMediana == null ? null : Math.round((a.resolucionMediana / 60) * 10) / 10],
    ['Resolución, 90 % de los casos (h)', d.resolucionP90 == null ? null : Math.round((d.resolucionP90 / 60) * 10) / 10, a?.resolucionP90 == null ? null : Math.round((a.resolucionP90 / 60) * 10) / 10],
    ['Horas registradas en casos', Math.round((d.horas / 60) * 100) / 100, a ? Math.round((a.horas / 60) * 100) / 100 : null],
  ];
}

export const etiquetasDias = (desde: Date, n: number) => Array.from({ length: n }, (_, i) => new Date(desde.getTime() + i * 86_400_000).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' }));
